/**
 * Property helpers for Grid AI automation.
 * Additive only — omit a field and existing defaults remain unchanged.
 */

export const NAMED_COLORS = {
  red: '#FF0000',
  blue: '#0000FF',
  green: '#00FF00',
  yellow: '#FFFF00',
  orange: '#FFA500',
  purple: '#800080',
  violet: '#8A2BE2',
  indigo: '#4B0082',
  pink: '#FFC0CB',
  cyan: '#00FFFF',
  magenta: '#FF00FF',
  white: '#FFFFFF',
  black: '#000000',
  gray: '#808080',
  grey: '#808080',
  brown: '#A52A2A',
  gold: '#FFD700',
  silver: '#C0C0C0',
  navy: '#000080',
  teal: '#008080',
  lime: '#00FF00',
  maroon: '#800000',
  olive: '#808000',
  aqua: '#00FFFF',
  skyblue: '#87CEEB',
  lightblue: '#ADD8E6',
  darkblue: '#00008B',
  darkred: '#8B0000',
  darkgreen: '#006400',
  darkpurple: '#4B0082',
  lightpurple: '#DDA0DD',
  darkorange: '#FF8C00',
  lightgreen: '#90EE90',
  lightpink: '#FFB6C1',
  lightgray: '#D3D3D3',
  lightgrey: '#D3D3D3',
  darkgray: '#A9A9A9',
  darkgrey: '#A9A9A9',
  darkcyan: '#008B8B',
  darkmagenta: '#8B008B',
  darkviolet: '#9400D3',
  deeppink: '#FF1493',
  hotpink: '#FF69B4',
};

/** Whitelist of create/modify fields forwarded from AI tool params. */
export const CREATE_PROP_KEYS = [
  'color',
  'position',
  'rotation',
  'scale',
  'quantity',
  'particleCount',
  'size',
  'opacity',
  'transparency',
  'transparent',
  'metalness',
  'roughness',
  'metallic',
  'emission',
  'emissive',
  'emissiveIntensity',
  'wireframe',
  'renderingMode',
  'side',
  'visible',
  'castShadow',
  'receiveShadow',
  'range',
  'height',
  'intensity',
  'windStrength',
  'turbulence',
  'life',
  'shape',
  'shadow',
  'angle',
  'width',
  'content',
  'fontSize',
  'textType',
  'fontFamily',
  'fontWeight',
  'buttonText',
  'messageText',
  'buttonColor',
  'messageBgColor',
  'extrusion',
  'relativeTo',
  'placement',
  'layout',
  'layoutAxis',
  'spacing',
  'layoutCols',
];

export const PARTICLE_TYPES = [
  'rain',
  'snow',
  'fire',
  'stars',
  'clouds',
  'water',
  'foliage',
];

/** Effects included when the user asks for "all particles" / "all effects". */
export const ADDABLE_PARTICLE_TYPES = [...PARTICLE_TYPES];

export const LIGHT_TYPES = [
  'light',
  'pointlight',
  'directionallight',
  'spotlight',
  'arealight',
  'ambientlight',
];

export const SHAPE_TYPES = [
  'cube',
  'sphere',
  'cylinder',
  'cone',
  'pyramid',
  'capsule',
  'torus',
  'rectangle',
  'ellipse',
  'triangle',
  'plane',
];

export const CONTENT_TYPES = ['text', 'hotspot'];

/**
 * Sanitize free-form text for text meshes / hotspot labels.
 */
export function sanitizeTextContent(input, fallback = 'Spatial Grid') {
  if (input == null) return fallback;
  const raw = String(input).trim();
  if (!raw) return fallback;
  // Strip wrapping quotes the model sometimes includes
  const unquoted = raw.replace(/^["'`]|["'`]$/g, '').trim();
  return (unquoted || fallback).slice(0, 500);
}

function clampByte(n) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function hexToRgb(hex) {
  const h = String(hex).replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHex(r, g, b) {
  return `#${[r, g, b]
    .map((v) => clampByte(v).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

/** Darken a #RRGGBB color toward black (amount 0–1). */
export function darkenHex(hex, amount = 0.4) {
  const { r, g, b } = hexToRgb(hex);
  const t = 1 - Math.max(0, Math.min(1, amount));
  return rgbToHex(r * t, g * t, b * t);
}

/** Lighten a #RRGGBB color toward white (amount 0–1). */
export function lightenHex(hex, amount = 0.4) {
  const { r, g, b } = hexToRgb(hex);
  const a = Math.max(0, Math.min(1, amount));
  return rgbToHex(r + (255 - r) * a, g + (255 - g) * a, b + (255 - b) * a);
}

function isResolvedHexColor(value) {
  return typeof value === 'string' && /^#[0-9A-F]{6}$/i.test(value.trim());
}

/**
 * Normalize color names / short hex into #RRGGBB when possible.
 * Handles "dark purple", "light blue", CSS names, and hex.
 */
export function normalizeColor(input) {
  if (input == null || input === '') return undefined;

  if (typeof input === 'number' && Number.isFinite(input)) {
    const hex = Math.max(0, Math.min(0xffffff, Math.floor(input)))
      .toString(16)
      .padStart(6, '0');
    return `#${hex.toUpperCase()}`;
  }

  const raw = String(input).trim();
  if (!raw) return undefined;

  const lower = raw.toLowerCase().trim();
  const compact = lower.replace(/[\s_-]+/g, '');

  if (NAMED_COLORS[compact]) return NAMED_COLORS[compact];
  if (NAMED_COLORS[lower]) return NAMED_COLORS[lower];

  // Modifier + base: "dark purple", "light blue", "deep red"
  const modMatch = lower.match(
    /^(dark|light|deep|bright|pale|hot)\s+([a-z]+)$/
  );
  if (modMatch) {
    const [, mod, base] = modMatch;
    const compound = NAMED_COLORS[`${mod}${base}`];
    if (compound) return compound;

    const baseHex = NAMED_COLORS[base];
    if (baseHex) {
      if (mod === 'dark' || mod === 'deep') return darkenHex(baseHex, 0.45);
      if (mod === 'light' || mod === 'pale') return lightenHex(baseHex, 0.45);
      if (mod === 'bright') return lightenHex(baseHex, 0.25);
      if (mod === 'hot' && base === 'pink') return NAMED_COLORS.hotpink;
    }
  }

  // #RGB → #RRGGBB
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    const r = raw[1];
    const g = raw[2];
    const b = raw[3];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }

  if (/^#[0-9a-f]{6}$/i.test(raw)) {
    return raw.toUpperCase();
  }

  // 0xRRGGBB or RRGGBB
  if (/^0x[0-9a-f]{6}$/i.test(raw)) {
    return `#${raw.slice(2).toUpperCase()}`;
  }
  if (/^[0-9a-f]{6}$/i.test(raw)) {
    return `#${raw.toUpperCase()}`;
  }

  // Leave css-like strings (rgb(...)) for Three.js Color parser
  return raw;
}

/**
 * Infer a #RRGGBB color from a natural-language command.
 * Prefers "dark purple" over bare "purple".
 */
export function inferColorFromCommand(userCommand) {
  const text = String(userCommand || '').toLowerCase();
  if (!text.trim()) return undefined;

  // 1) Modifier + color phrase
  const modPhrase = text.match(
    /\b(dark|light|deep|bright|pale|hot)\s+([a-z]+)\b/
  );
  if (modPhrase) {
    const candidate = normalizeColor(`${modPhrase[1]} ${modPhrase[2]}`);
    if (isResolvedHexColor(candidate)) return candidate;
  }

  // 2) Longest named color first (darkblue before blue)
  const keys = Object.keys(NAMED_COLORS).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    const spaced = key.replace(/^(dark|light|sky|deep|hot)/, '$1 ');
    const patterns = [
      new RegExp(`\\b${key}\\b`, 'i'),
      spaced !== key ? new RegExp(`\\b${spaced.trim()}\\b`, 'i') : null,
    ].filter(Boolean);

    if (patterns.some((re) => re.test(text))) {
      return NAMED_COLORS[key];
    }
  }

  return undefined;
}

/**
 * Infer rotation in degrees from phrases like:
 * "rotated 90° on X axis", "rotate 45 degrees around y", "pitch 30".
 *
 * Bare angles ("-45deg rotated") with NO axis → apply that angle to X, Y, and Z.
 * Returns { x?, y?, z? } in degrees, or null.
 */
export function inferRotationFromCommand(userCommand) {
  return parseRotationFromText(userCommand);
}

/**
 * Shared rotation parser.
 * @param {string} userCommand
 * @param {{ defaultAxis?: 'x'|'y'|'z'|'all'|null }} [options]
 *   defaultAxis — 'all' (default) applies bare angles to x,y,z; pass null to skip bare angles.
 */
export function parseRotationFromText(
  userCommand,
  { defaultAxis = 'all' } = {}
) {
  const text = String(userCommand || '').toLowerCase();
  if (!text.trim()) return null;

  const rotation = {};
  const apply = (axis, value) => {
    if (!axis || !Number.isFinite(value)) return;
    rotation[String(axis).toLowerCase()] = value;
  };

  // pitch / yaw / roll vocabulary
  const pitch = text.match(/\bpitch\s*[:=]?\s*(-?\d+(?:\.\d+)?)/i);
  if (pitch) apply('x', parseFloat(pitch[1]));
  const yaw = text.match(/\byaw\s*[:=]?\s*(-?\d+(?:\.\d+)?)/i);
  if (yaw) apply('y', parseFloat(yaw[1]));
  const roll = text.match(/\broll\s*[:=]?\s*(-?\d+(?:\.\d+)?)/i);
  if (roll) apply('z', parseFloat(roll[1]));

  const patterns = [
    /rotat(?:ed|e|ion)?\s*[:=]?\s*(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:rees?)?)?\s*(?:on|around|about|along|in)\s*(?:the\s+)?([xyz])(?:[\s-]?axis)?/i,
    /(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:rees?)?)\s*(?:on|around|about|along)\s*(?:the\s+)?([xyz])(?:[\s-]?axis)?/i,
    /\b([xyz])(?:[\s-]?axis)?\s*(?:rotation|rotated|rot)\s*[:=]?\s*(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:grees?)?)?/i,
    /\b(?:rotation|rotated|rot)\s+[:=]?\s*([xyz])\s*[:=]?\s*(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:grees?)?)?/i,
  ];

  for (let i = 0; i < patterns.length; i++) {
    const m = text.match(patterns[i]);
    if (!m) continue;
    if (i <= 1) apply(m[2], parseFloat(m[1]));
    else apply(m[1], parseFloat(m[2]));
    break;
  }

  const multi = [
    ...text.matchAll(
      /\b([xyz])\s*[:=]\s*(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:rees?)?)?/gi
    ),
  ];
  multi.forEach((m) => apply(m[1], parseFloat(m[2])));

  // Bare "45deg" / "rotated -45" with no axis → all three axes (unless disabled)
  if (!Object.keys(rotation).length && defaultAxis) {
    const bare = text.match(
      /(?:rotat(?:ed|e|ion)?\s*[:=]?\s*)?(-?\d+(?:\.\d+)?)\s*(?:°|deg(?:rees?)?)/i
    );
    if (bare) {
      const angle = parseFloat(bare[1]);
      if (defaultAxis === 'all') {
        apply('x', angle);
        apply('y', angle);
        apply('z', angle);
      } else {
        apply(defaultAxis, angle);
      }
    }
  }

  return Object.keys(rotation).length ? rotation : null;
}

const WORD_QTY_FOR_SNIPPET =
  'one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|twenty|thirty';

function objectTypePattern(objectType) {
  const type = String(objectType || '')
    .toLowerCase()
    .replace(/s$/, '');
  if (type === 'torus') return '(?:torus|tori|toruses)';
  if (type === 'box' || type === 'cube') return '(?:cube|cubes|box|boxes)';
  return `${type}s?`;
}

/**
 * Collect command text that refers to a specific object type
 * (declaration clause + "all the cubes should…" follow-ups).
 */
export function getCommandSnippetForObjectType(userCommand, objectType) {
  const original = String(userCommand || '');
  if (!original.trim() || !objectType) return original;

  const typePat = objectTypePattern(objectType);
  const shapeAlt = SHAPE_TYPES.map((s) =>
    s === 'torus' ? 'tori|toruses?|torus' : `${s}s?`
  ).join('|');
  const qtyShape = `(?:\\d{1,3}|${WORD_QTY_FOR_SNIPPET})\\s+(?:[\\w-]+\\s+){0,6}(?:${shapeAlt})`;
  const chunks = [];

  const declRe = new RegExp(
    `\\b(?:\\d{1,3}|${WORD_QTY_FOR_SNIPPET})\\s+(?:[\\w-]+\\s+){0,6}${typePat}\\b[\\s\\S]*?(?=\\b${qtyShape}\\b|,\\s*all\\b|$)`,
    'gi'
  );
  for (const m of original.matchAll(declRe)) {
    if (m[0]?.trim()) chunks.push(m[0].trim());
  }

  const allRe = new RegExp(
    `\\ball\\s+(?:the\\s+)?${typePat}\\b[\\s\\S]*?(?=\\band\\s+all\\b|[.]|$)`,
    'gi'
  );
  for (const m of original.matchAll(allRe)) {
    if (m[0]?.trim()) chunks.push(m[0].trim());
  }

  const shouldRe = new RegExp(
    `\\b${typePat}\\s+should\\s+be\\b[\\s\\S]*?(?=\\band\\s+(?:all\\s+)?(?:the\\s+)?(?:${shapeAlt})\\b|[.]|$)`,
    'gi'
  );
  for (const m of original.matchAll(shouldRe)) {
    if (m[0]?.trim()) chunks.push(m[0].trim());
  }

  return chunks.length ? chunks.join(' ') : original;
}

export function inferColorForObjectType(userCommand, objectType) {
  return inferColorFromCommand(
    getCommandSnippetForObjectType(userCommand, objectType)
  );
}

export function inferRotationForObjectType(userCommand, objectType) {
  return parseRotationFromText(
    getCommandSnippetForObjectType(userCommand, objectType)
  );
}

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function pickFromSource(source, out) {
  if (!isPlainObject(source)) return;

  for (const key of CREATE_PROP_KEYS) {
    if (source[key] === undefined || source[key] === null) continue;
    if (out[key] !== undefined) continue;
    out[key] = source[key];
  }

  // Flat aliases AI sometimes emits
  if (out.particleCount === undefined) {
    const count =
      source.count ?? source.particle_count ?? source.numParticles;
    if (count !== undefined && count !== null) out.particleCount = count;
  }
  if (out.windStrength === undefined && source.wind != null) {
    if (typeof source.wind === 'number') out.windStrength = source.wind;
    else if (isPlainObject(source.wind) && source.wind.x != null) {
      out.windStrength = Math.abs(Number(source.wind.x)) || undefined;
    }
  }
}

/**
 * Merge nested `properties` + top-level tool params into a whitelist-only object.
 * Normalizes color when present. Does not invent defaults.
 */
export function pickCreateProps(parameters) {
  const raw = isPlainObject(parameters) ? parameters : {};
  const nested = isPlainObject(raw.properties) ? raw.properties : {};
  const out = {};

  // Nested first, then top-level overrides (top-level wins)
  pickFromSource(nested, out);
  const topOnly = { ...raw };
  delete topOnly.properties;
  delete topOnly.objectType;
  delete topOnly.name;
  delete topOnly.type;
  delete topOnly.query;
  pickFromSource(topOnly, out);
  // Re-apply top-level explicitly so it wins over nested for same keys
  for (const key of CREATE_PROP_KEYS) {
    if (topOnly[key] !== undefined && topOnly[key] !== null) {
      out[key] = topOnly[key];
    }
  }

  if (out.color !== undefined) {
    const normalized = normalizeColor(out.color);
    if (normalized !== undefined) out.color = normalized;
    else delete out.color;
  }

  if (out.particleCount !== undefined) {
    const n = Number(out.particleCount);
    if (Number.isFinite(n) && n > 0) {
      out.particleCount = Math.min(Math.floor(n), 50000);
    } else {
      delete out.particleCount;
    }
  }

  if (out.intensity !== undefined) {
    const n = Number(out.intensity);
    if (Number.isFinite(n)) out.intensity = n;
    else delete out.intensity;
  }

  for (const numKey of [
    'size',
    'opacity',
    'range',
    'height',
    'windStrength',
    'turbulence',
    'life',
    'angle',
    'width',
    'fontSize',
    'extrusion',
    'metalness',
    'roughness',
    'emissiveIntensity',
  ]) {
    if (out[numKey] === undefined) continue;
    const n = Number(out[numKey]);
    if (Number.isFinite(n)) out[numKey] = n;
    else delete out[numKey];
  }

  // Normalize emission aliases to hex when present
  for (const key of ['emission', 'emissive']) {
    if (out[key] === undefined) continue;
    const normalized = normalizeColor(out[key]);
    if (normalized) out[key] = normalized;
    else delete out[key];
  }

  // Coerce boolean-ish flags
  for (const key of [
    'wireframe',
    'visible',
    'castShadow',
    'receiveShadow',
    'transparency',
    'transparent',
  ]) {
    if (out[key] === undefined || out[key] === null) continue;
    if (typeof out[key] === 'boolean') continue;
    const s = String(out[key]).toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(s)) out[key] = true;
    else if (['false', '0', 'no', 'off'].includes(s)) out[key] = false;
    else delete out[key];
  }

  // Nested metallic { metalness, roughness }
  if (isPlainObject(out.metallic)) {
    const m = { ...out.metallic };
    if (m.metalness != null) {
      const n = Number(m.metalness);
      if (Number.isFinite(n)) m.metalness = Math.max(0, Math.min(1, n));
      else delete m.metalness;
    }
    if (m.roughness != null) {
      const n = Number(m.roughness);
      if (Number.isFinite(n)) m.roughness = Math.max(0, Math.min(1, n));
      else delete m.roughness;
    }
    out.metallic = m;
  }

  if (out.quantity !== undefined) {
    const n = Number(out.quantity);
    if (Number.isFinite(n) && n >= 1) {
      out.quantity = Math.min(Math.floor(n), 100);
    } else {
      delete out.quantity;
    }
  }

  return out;
}

/**
 * Detect color / intensity / density / text-content hints from free-text and inject
 * into tool calls when the model omitted them. Never overwrites an existing value.
 */
export function enrichToolCallsFromCommand(userCommand, toolCalls) {
  if (!Array.isArray(toolCalls) || toolCalls.length === 0) return toolCalls;

  const original = String(userCommand || '');
  const text = original.toLowerCase();
  if (!text.trim()) return toolCalls;

  const inferredColor = inferColorFromCommand(original);
  const inferredRotation = inferRotationFromCommand(original);
  const inferredOpacity = inferOpacityFromCommand(original);
  const inferredMaterial = inferMaterialPropsFromCommand(original);
  const inferredScale = inferScaleFromCommand(original);

  let inferredIntensity;
  if (/\b(dim|dimmed|soft|low)\b/.test(text)) inferredIntensity = 0.35;
  else if (/\b(bright|strong|intense|high)\b/.test(text))
    inferredIntensity = 2.5;

  let inferredParticleCount;
  if (/\b(heavy|dense|lots of|many)\b/.test(text))
    inferredParticleCount = 2500;
  else if (/\b(light|sparse|few|gentle)\b/.test(text))
    inferredParticleCount = 400;

  let inferredParticleSize;
  if (/\b(big|large|huge)\b/.test(text)) inferredParticleSize = 0.25;
  else if (/\b(small|tiny)\b/.test(text)) inferredParticleSize = 0.05;

  let inferredFontSize;
  if (/\b(huge|giant|massive)\b/.test(text)) inferredFontSize = 160;
  else if (/\b(big|large)\b/.test(text)) inferredFontSize = 120;
  else if (/\b(small|tiny)\b/.test(text)) inferredFontSize = 40;

  // Quoted content: add text "Hello World" / saying 'Welcome'
  const quoted =
    original.match(/["']([^"']{1,500})["']/) ||
    original.match(
      /\b(?:saying|that says|reading|with text|labelled|labeled)\s+["']?([^"'.,!]{1,200})["']?/i
    );
  let inferredContent = quoted
    ? sanitizeTextContent(quoted[1], '')
    : undefined;

  // Unquoted: "add blue text Hello World" / "add 3d text Sale"
  if (!inferredContent && /\btext\b/.test(text)) {
    const afterText = original.match(/\btext\b(?:\s+(?:2d|3d))?\s+(.+)$/i);
    if (afterText) {
      const noise = new Set([
        'a',
        'an',
        'the',
        'to',
        'scene',
        'here',
        'please',
        '2d',
        '3d',
        ...Object.keys(NAMED_COLORS).map((k) => k.toLowerCase()),
        'large',
        'big',
        'huge',
        'small',
        'tiny',
        'giant',
        'massive',
      ]);
      const words = afterText[1]
        .trim()
        .split(/\s+/)
        .filter((w) => {
          const clean = w.toLowerCase().replace(/[^a-z0-9]/gi, '');
          return clean && !noise.has(clean);
        });
      if (words.length) {
        inferredContent = sanitizeTextContent(words.join(' '), '');
      }
    }
  }

  // Hotspot label from "add hotspot Info" when not quoted
  let inferredHotspotLabel;
  if (/\bhot\s*-?\s*spot\b/.test(text) && !inferredContent) {
    const afterHs = original.match(/\bhot\s*-?\s*spot\b\s+(.+)$/i);
    if (afterHs) {
      const rest = afterHs[1]
        .replace(/\b(?:with\s+)?(?:message|saying|popup)\s+.+$/i, '')
        .replace(/\b(?:labelled|labeled|named|called|label)\s+/i, '')
        .trim();
      const skip = new Set([
        'a',
        'an',
        'the',
        'to',
        'scene',
        'here',
        'please',
        'circle',
        'rectangle',
        ...Object.keys(NAMED_COLORS).map((k) => k.toLowerCase()),
      ]);
      const words = rest
        .split(/\s+/)
        .filter((w) => !skip.has(w.toLowerCase()));
      if (words.length) {
        inferredHotspotLabel = sanitizeTextContent(words.join(' '), '');
      }
    }
  }

  const hotspotMsgMatch = original.match(
    /\b(?:with\s+)?(?:message|saying|popup)\s+["']?([^"'.,!]{1,300})["']?/i
  );
  const inferredHotspotMessage = hotspotMsgMatch
    ? sanitizeTextContent(hotspotMsgMatch[1], '')
    : undefined;

  const addObjectCount = toolCalls.filter(
    (c) => c && c.name === 'add_object'
  ).length;
  const addObjectTypes = [
    ...new Set(
      toolCalls
        .filter((c) => c && c.name === 'add_object')
        .map((c) =>
          String(c.parameters?.objectType || '')
            .toLowerCase()
            .trim()
        )
        .filter(Boolean)
    ),
  ];
  const multiTypeAdds = addObjectTypes.length > 1;
  const angleCount = (
    original.match(/-?\d+(?:\.\d+)?\s*(?:°|deg(?:rees?)?)/gi) || []
  ).length;
  const colorNameHits = Object.keys(NAMED_COLORS).filter((k) =>
    new RegExp(`\\b${k.replace(/\s+/g, '')}\\b`, 'i').test(
      text.replace(/\s+/g, '')
    ) || new RegExp(`\\b${k}\\b`, 'i').test(text)
  ).length;
  // When the user describes distinct individuals, don't stamp one global
  // color/rotation onto every add_object call.
  const skipGlobalAppearanceOnAdds =
    addObjectCount > 1 && (angleCount > 1 || colorNameHits > 1);

  return toolCalls.map((call) => {
    if (!call || typeof call !== 'object') return call;

    const name = call.name;
    if (
      name !== 'add_object' &&
      name !== 'modify_object' &&
      name !== 'add_text' &&
      name !== 'add_hotspot'
    ) {
      return call;
    }

    const params = {
      ...(isPlainObject(call.parameters) ? call.parameters : {}),
    };
    const props = isPlainObject(params.properties)
      ? { ...params.properties }
      : {};

    const hasColor = params.color != null || props.color != null;
    const hasOpacity =
      params.opacity != null ||
      props.opacity != null ||
      (isPlainObject(params.properties) && params.properties.opacity != null);
    const hasMetalness =
      params.metalness != null ||
      props.metalness != null ||
      params.metallic?.metalness != null ||
      props.metallic?.metalness != null;
    const hasRoughness =
      params.roughness != null ||
      props.roughness != null ||
      params.metallic?.roughness != null ||
      props.metallic?.roughness != null;
    const hasEmission =
      params.emission != null ||
      params.emissive != null ||
      props.emission != null ||
      props.emissive != null;
    const hasWireframe =
      params.wireframe != null ||
      props.wireframe != null ||
      params.renderingMode != null;
    const hasScale =
      params.scale != null ||
      props.scale != null ||
      params.size != null ||
      props.size != null;
    const hasVisible = params.visible != null || props.visible != null;
    const hasIntensity =
      params.intensity != null || props.intensity != null;
    const hasCount =
      params.particleCount != null || props.particleCount != null;
    const hasSize = params.size != null || props.size != null;
    const hasFontSize =
      params.fontSize != null || props.fontSize != null;
    const hasContent =
      params.content != null ||
      params.text != null ||
      props.content != null;
    const hasButtonText = params.buttonText != null;
    const hasMessageText = params.messageText != null;

    const objectType = String(
      params.objectType || params.type || ''
    ).toLowerCase();
    const useScopedAppearance = multiTypeAdds && name === 'add_object';
    const colorToApply = useScopedAppearance
      ? inferColorForObjectType(original, objectType)
      : inferredColor;
    const rotationToApply = useScopedAppearance
      ? inferRotationForObjectType(original, objectType)
      : inferredRotation;
    const skipAppearanceStamp =
      name === 'add_object' &&
      skipGlobalAppearanceOnAdds &&
      !useScopedAppearance;
    const isParticle = PARTICLE_TYPES.includes(objectType);
    const isLight = LIGHT_TYPES.includes(objectType);
    const isTextTool =
      name === 'add_text' ||
      objectType === 'text' ||
      (name === 'modify_object' && objectType === 'text');
    const isHotspotTool =
      name === 'add_hotspot' || objectType === 'hotspot';

    // Always coerce color to a real #RRGGBB. AI often sends "dark purple"
    // which Three.js cannot parse — normalize or fall back to inferred.
    if (hasColor) {
      const existing = params.color ?? props.color;
      const normalized = normalizeColor(existing);
      if (isResolvedHexColor(normalized)) {
        params.color = normalized;
      } else if (colorToApply) {
        params.color = colorToApply;
      } else if (normalized) {
        params.color = normalized;
      }
    } else if (colorToApply && !skipAppearanceStamp) {
      params.color = colorToApply;
    }

    // Opacity: keep 0 (falsy) — use != null checks only
    if (hasOpacity) {
      const existing = params.opacity ?? props.opacity;
      const n = Number(existing);
      if (Number.isFinite(n)) {
        params.opacity = Math.max(0, Math.min(1, n > 1 ? n / 100 : n));
      } else if (inferredOpacity != null) {
        params.opacity = inferredOpacity;
      }
    } else if (inferredOpacity != null) {
      params.opacity = inferredOpacity;
    }

    // Material surface props (metal / rough / glow / wireframe)
    if (inferredMaterial) {
      if (!hasMetalness && inferredMaterial.metalness != null) {
        params.metalness = inferredMaterial.metalness;
      }
      if (!hasRoughness && inferredMaterial.roughness != null) {
        params.roughness = inferredMaterial.roughness;
      }
      if (!hasEmission && inferredMaterial.emission != null) {
        params.emission =
          inferredMaterial.emission === true
            ? params.color || inferredColor || '#FFFFFF'
            : inferredMaterial.emission;
      }
      if (!hasEmission && inferredMaterial.emissiveIntensity != null) {
        params.emissiveIntensity = inferredMaterial.emissiveIntensity;
      }
      if (!hasWireframe && inferredMaterial.wireframe != null) {
        params.wireframe = inferredMaterial.wireframe;
      }
      if (!hasVisible && inferredMaterial.visible != null) {
        params.visible = inferredMaterial.visible;
      }
      if (inferredMaterial.castShadow != null && params.castShadow == null) {
        params.castShadow = inferredMaterial.castShadow;
      }
      if (inferredMaterial.side != null && params.side == null) {
        params.side = inferredMaterial.side;
      }
    }

    // If glowing but no emission color, use albedo/inferred color
    if (
      params.emissiveIntensity != null &&
      params.emission == null &&
      params.emissive == null
    ) {
      params.emission = params.color || inferredColor || '#FFFFFF';
    }

    // Shape scale from "large/small" or numeric size (shapes only — not particles)
    const isShape = SHAPE_TYPES.includes(objectType);
    if (isShape && !hasScale) {
      if (inferredScale != null) {
        params.scale = { x: inferredScale, y: inferredScale, z: inferredScale };
      }
    }
    // Explicit size on a shape → uniform scale when scale omitted
    if (
      isShape &&
      params.scale == null &&
      props.scale == null &&
      (params.size != null || props.size != null)
    ) {
      const s = Number(params.size ?? props.size);
      if (Number.isFinite(s) && s > 0) {
        params.scale = { x: s, y: s, z: s };
      }
    }

    // Rotation from "rotated 90° on X axis" — merge into params.rotation
    // Skip global stamp when multiple adds have multiple angles (one/other cases).
    // Multi-type adds use per-type rotation instead.
    if (
      rotationToApply &&
      (name === 'add_object' || name === 'modify_object' || name === 'add_text') &&
      !skipAppearanceStamp
    ) {
      const existingRot = isPlainObject(params.rotation)
        ? params.rotation
        : isPlainObject(props.rotation)
          ? props.rotation
          : {};
      params.rotation = {
        x: existingRot.x ?? rotationToApply.x ?? 0,
        y: existingRot.y ?? rotationToApply.y ?? 0,
        z: existingRot.z ?? rotationToApply.z ?? 0,
      };
      for (const axis of ['x', 'y', 'z']) {
        if (rotationToApply[axis] != null) {
          params.rotation[axis] = rotationToApply[axis];
        }
      }
    } else if (
      name === 'add_object' &&
      skipAppearanceStamp &&
      isPlainObject(params.rotation)
    ) {
      // keep per-call rotation as-is
    }

    if (!hasIntensity && inferredIntensity != null && isLight) {
      params.intensity = inferredIntensity;
    }
    if (!hasCount && inferredParticleCount != null && isParticle) {
      params.particleCount = inferredParticleCount;
    }
    if (!hasSize && inferredParticleSize != null && isParticle) {
      params.size = inferredParticleSize;
    }
    if (!hasFontSize && inferredFontSize != null && isTextTool) {
      params.fontSize = inferredFontSize;
    }
    if (!hasContent && inferredContent && isTextTool) {
      params.content = inferredContent;
    }
    if (isHotspotTool) {
      const label = inferredHotspotLabel || inferredContent;
      if (!hasButtonText && label) params.buttonText = label.slice(0, 40);
      if (!hasMessageText && inferredHotspotMessage)
        params.messageText = inferredHotspotMessage;
      else if (!hasMessageText && inferredContent)
        params.messageText = inferredContent;
    }
    // "2d text" / "3d text"
    if (isTextTool && params.textType == null) {
      if (/\b2d\b/.test(text)) params.textType = '2D';
      else if (/\b3d\b/.test(text)) params.textType = '3D';
    }

    // "make all cubes orange" / "paint every sphere red" / plural "make cubes blue"
    if (name === 'modify_object') {
      const type = objectType || String(params.name || '').toLowerCase();
      const pluralHit =
        type &&
        new RegExp(`\\b${type.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}s\\b`, 'i').test(
          original
        );
      const allHit =
        /\b(?:all|every|each|every\s+single)\b/i.test(original) ||
        pluralHit;
      if (
        allHit ||
        params.all === true ||
        params.all === 'true' ||
        Number(params.quantity) === -1
      ) {
        params.all = true;
      }
      // Drop name filters that are just the type/plural — they'd block multi-match
      if (params.all && params.name) {
        const n = String(params.name).toLowerCase().trim();
        if (
          !type ||
          n === type ||
          n === `${type}s` ||
          n === 'all' ||
          n === `all ${type}` ||
          n === `all ${type}s` ||
          n === `every ${type}` ||
          n === `every ${type}s`
        ) {
          delete params.name;
        }
      }
    }

    if (Object.keys(props).length) {
      params.properties = props;
    }

    return { ...call, parameters: params };
  });
}

/**
 * Infer opacity 0–1 from phrases like:
 * "opacity 0", "opacity: 0.5", "50% opacity", "transparent", "fully transparent".
 */
export function inferOpacityFromCommand(userCommand) {
  const text = String(userCommand || '').toLowerCase();
  if (!text.trim()) return undefined;

  const explicit = text.match(
    /\b(?:opacity|alpha|transparency)\s*[:=]?\s*(-?\d+(?:\.\d+)?)\s*%?/i
  );
  if (explicit) {
    let n = parseFloat(explicit[1]);
    if (!Number.isFinite(n)) return undefined;
    // "opacity 50" or "50%" → 0.5; already 0–1 stays as-is
    if (n > 1) n = n / 100;
    return Math.max(0, Math.min(1, n));
  }

  const percent = text.match(/\b(\d+(?:\.\d+)?)\s*%\s*(?:opacity|alpha|transparent)?/i);
  if (percent) {
    const n = parseFloat(percent[1]);
    if (Number.isFinite(n)) return Math.max(0, Math.min(1, n / 100));
  }

  if (/\b(fully|completely|totally)\s+transparent\b/.test(text)) return 0;
  if (/\binvisible\b/.test(text)) return 0;
  if (/\bsemi[\s-]?transparent\b|\btranslucent\b/.test(text)) return 0.5;
  // Bare "transparent" without a number → glass-like default (explicit "opacity N" wins above)
  if (/\btransparent\b/.test(text)) return 0.35;

  return undefined;
}

/**
 * Apply opacity to a mesh/group and sync material_settings for the editor panel.
 * Opacity 0 requires transparent=true or Three.js ignores it.
 */
export function applyMeshOpacity(object, opacity) {
  if (!object || opacity == null) return;
  const op = Number(opacity);
  if (!Number.isFinite(op)) return;
  const clamped = Math.max(0, Math.min(1, op));
  const needsTransparent = clamped < 0.999;

  const applyToMaterial = (material) => {
    if (!material) return;
    const materials = Array.isArray(material) ? material : [material];
    materials.forEach((mat) => {
      if (!mat) return;
      mat.opacity = clamped;
      mat.transparent = needsTransparent;
      if (needsTransparent) mat.depthWrite = false;
      else mat.depthWrite = true;
      mat.needsUpdate = true;
    });
  };

  if (object.material) applyToMaterial(object.material);
  if (typeof object.traverse === 'function') {
    object.traverse((child) => {
      if (child.isMesh && child.material) applyToMaterial(child.material);
    });
  }

  object.userData = object.userData || {};
  object.userData.material_settings = {
    ...(object.userData.material_settings || {}),
    opacity: clamped,
    alpha: clamped,
    transparency: needsTransparent,
    transparent: needsTransparent,
  };
}

/**
 * Infer metalness / roughness / emission / wireframe / visibility from natural language.
 */
export function inferMaterialPropsFromCommand(userCommand) {
  const text = String(userCommand || '').toLowerCase();
  if (!text.trim()) return null;

  const out = {};

  const metalMatch = text.match(
    /\b(?:metalness|metallic)\s*[:=]?\s*(-?\d+(?:\.\d+)?)/i
  );
  if (metalMatch) {
    let n = parseFloat(metalMatch[1]);
    if (n > 1) n = n / 100;
    if (Number.isFinite(n)) {
      out.metalness = Math.max(0, Math.min(1, n));
      out.roughness = out.roughness ?? 0.2;
    }
  } else if (/\b(chrome|metallic|metal|shiny|polished|mirror(?:ed)?)\b/.test(text)) {
    out.metalness = 0.95;
    out.roughness = out.roughness ?? 0.15;
  }

  const roughMatch = text.match(
    /\b(?:roughness|rough)\s*[:=]?\s*(-?\d+(?:\.\d+)?)/i
  );
  if (roughMatch) {
    let n = parseFloat(roughMatch[1]);
    if (n > 1) n = n / 100;
    if (Number.isFinite(n)) out.roughness = Math.max(0, Math.min(1, n));
  } else if (/\b(matte|dull|rough|frosted)\b/.test(text)) {
    out.roughness = 0.9;
    out.metalness = out.metalness ?? 0.05;
  } else if (/\b(glossy|smooth|shiny)\b/.test(text) && out.roughness == null) {
    out.roughness = 0.2;
  }

  const emisMatch = text.match(
    /\b(?:emissive(?:Intensity)?|emission|glow)\s*[:=]?\s*(-?\d+(?:\.\d+)?)/i
  );
  if (emisMatch && !/\bglowing\b/.test(emisMatch[0])) {
    const n = parseFloat(emisMatch[1]);
    if (Number.isFinite(n)) {
      out.emissiveIntensity = Math.max(0, Math.min(5, n > 5 ? n / 100 : n));
    }
  }
  if (/\b(glowing|emissive|neon|lit|luminous)\b/.test(text)) {
    out.emissiveIntensity = out.emissiveIntensity ?? 1.2;
    // emission color filled later from object color in enrich
    out.emission = out.emission ?? true;
  }

  if (/\bwireframe\b/.test(text)) {
    out.wireframe = true;
  }

  if (/\b(double[\s-]?sided|both\s+sides)\b/.test(text)) {
    out.side = 'Double';
  }

  if (/\b(hidden|hide|invisible)\b/.test(text) && !/\bopacity\b/.test(text)) {
    // Prefer opacity 0 for "invisible"; visible=false only when explicitly hidden
    if (/\b(hide|hidden)\b/.test(text)) out.visible = false;
  }

  if (/\b(no\s+shadow|without\s+shadow)\b/.test(text)) {
    out.castShadow = false;
  } else if (/\b(with\s+shadow|cast\s+shadow)\b/.test(text)) {
    out.castShadow = true;
  }

  return Object.keys(out).length ? out : null;
}

/**
 * Infer uniform scale for shapes: "large cube", "scale 2", "twice as big".
 */
export function inferScaleFromCommand(userCommand) {
  const text = String(userCommand || '').toLowerCase();
  if (!text.trim()) return undefined;

  const explicit = text.match(
    /\b(?:scale|scaled?)\s*[:=]?\s*(-?\d+(?:\.\d+)?)/i
  );
  if (explicit) {
    const n = parseFloat(explicit[1]);
    if (Number.isFinite(n) && n > 0) return n;
  }

  if (/\b(huge|giant|massive)\b/.test(text)) return 3;
  if (/\b(big|large)\b/.test(text)) return 2;
  if (/\b(tiny|mini)\b/.test(text)) return 0.35;
  if (/\b(small)\b/.test(text)) return 0.5;
  return undefined;
}

function forEachMeshMaterial(object, fn) {
  if (!object || typeof fn !== 'function') return;
  const visit = (mat) => {
    if (!mat) return;
    if (Array.isArray(mat)) mat.forEach(visit);
    else fn(mat);
  };
  if (object.material) visit(object.material);
  if (typeof object.traverse === 'function') {
    object.traverse((child) => {
      if (child.isMesh && child.material) visit(child.material);
    });
  }
}

function syncMaterialSettings(object, patch) {
  if (!object || !patch) return;
  object.userData = object.userData || {};
  const prev = object.userData.material_settings || {};
  const metallic = {
    ...(prev.metallic && typeof prev.metallic === 'object' ? prev.metallic : {}),
    ...(patch.metallic && typeof patch.metallic === 'object' ? patch.metallic : {}),
  };
  object.userData.material_settings = {
    ...prev,
    ...patch,
    metallic: Object.keys(metallic).length ? metallic : prev.metallic,
  };
}

/**
 * Apply all visual create/modify props onto a mesh or shape group.
 * Additive: only fields present on props are written.
 * Safe for opacity 0 and metalness 0 (uses != null checks).
 */
export function applyMeshVisualProps(object, rawProps = {}) {
  if (!object || !isPlainObject(rawProps)) return;

  const props = rawProps;

  if (props.color != null) {
    applyMeshColor(object, props.color);
  }

  // Opacity / transparency
  let opacity = props.opacity;
  if (opacity == null && (props.transparency === true || props.transparent === true)) {
    opacity = 0.35;
  }
  if (opacity != null) {
    applyMeshOpacity(object, opacity);
  } else if (props.transparency === false || props.transparent === false) {
    applyMeshOpacity(object, 1);
  }

  const metalness =
    props.metalness != null
      ? Number(props.metalness)
      : props.metallic?.metalness != null
        ? Number(props.metallic.metalness)
        : undefined;
  const roughness =
    props.roughness != null
      ? Number(props.roughness)
      : props.metallic?.roughness != null
        ? Number(props.metallic.roughness)
        : undefined;

  const emissionRaw =
    props.emission === true
      ? props.color || '#FFFFFF'
      : props.emission != null
        ? props.emission
        : props.emissive != null
          ? props.emissive
          : undefined;
  const emission =
    emissionRaw != null ? normalizeColor(emissionRaw) || emissionRaw : undefined;
  const emissiveIntensity =
    props.emissiveIntensity != null ? Number(props.emissiveIntensity) : undefined;

  const wireframe =
    props.wireframe === true ||
    String(props.renderingMode || '').toLowerCase() === 'wireframe';

  if (
    metalness != null ||
    roughness != null ||
    emission != null ||
    emissiveIntensity != null ||
    wireframe ||
    props.side != null
  ) {
    forEachMeshMaterial(object, (mat) => {
      if (Number.isFinite(metalness) && mat.metalness !== undefined) {
        mat.metalness = Math.max(0, Math.min(1, metalness));
      }
      if (Number.isFinite(roughness) && mat.roughness !== undefined) {
        // Match editor floor of ~0.05 when > 0; allow exact 0 if requested
        mat.roughness =
          roughness <= 0 ? 0 : Math.max(0.05, Math.min(1, roughness));
      }
      if (emission != null && mat.emissive && typeof mat.emissive.set === 'function') {
        try {
          mat.emissive.set(emission);
        } catch (_) {
          /* ignore */
        }
        if (mat.emissiveIntensity !== undefined) {
          mat.emissiveIntensity = Number.isFinite(emissiveIntensity)
            ? emissiveIntensity
            : Math.max(mat.emissiveIntensity || 0, 0.5);
        }
      } else if (
        Number.isFinite(emissiveIntensity) &&
        mat.emissiveIntensity !== undefined
      ) {
        mat.emissiveIntensity = emissiveIntensity;
      }
      if (wireframe && mat.wireframe !== undefined) {
        mat.wireframe = true;
      }
      if (props.side != null && mat.side !== undefined) {
        const s = String(props.side).toLowerCase();
        if (s === 'double' || s === '2' || s === 'doubleside') mat.side = 2; // THREE.DoubleSide
        else if (s === 'back') mat.side = 1;
        else mat.side = 0;
      }
      mat.needsUpdate = true;
    });

    const metallicPatch = {};
    if (Number.isFinite(metalness)) metallicPatch.metalness = metalness;
    if (Number.isFinite(roughness)) metallicPatch.roughness = roughness;
    syncMaterialSettings(object, {
      ...(Object.keys(metallicPatch).length ? { metallic: metallicPatch } : {}),
      ...(emission != null ? { emission } : {}),
      ...(wireframe ? { renderingMode: 'Wireframe' } : {}),
      ...(props.side != null ? { side: props.side } : {}),
    });
  }

  if (props.visible != null) {
    object.visible = !!props.visible;
  }

  if (props.castShadow != null || props.receiveShadow != null) {
    const applyShadow = (obj) => {
      if (!obj || !obj.isMesh) return;
      if (props.castShadow != null) obj.castShadow = !!props.castShadow;
      if (props.receiveShadow != null) obj.receiveShadow = !!props.receiveShadow;
    };
    applyShadow(object);
    if (typeof object.traverse === 'function') {
      object.traverse(applyShadow);
    }
  }
}

/**
 * Apply a color to a mesh or group (Capsule / Rectangle wrappers).
 * Also syncs userData.material_settings.albedo so the editor panel stays correct.
 */
export function applyMeshColor(object, color) {
  if (!object || color == null) return;
  const normalized = normalizeColor(color);
  if (!normalized) return;

  // Prefer hex — Three.js may reject names like "dark purple"
  const colorValue = isResolvedHexColor(normalized)
    ? normalized
    : normalizeColor(String(normalized).replace(/\s+/g, '')) || normalized;

  const applyToMaterial = (material) => {
    if (!material) return;
    const materials = Array.isArray(material) ? material : [material];
    materials.forEach((mat) => {
      if (!mat || !mat.color || typeof mat.color.set !== 'function') return;
      try {
        mat.color.set(colorValue);
        mat.needsUpdate = true;
      } catch (_) {
        /* ignore invalid color strings */
      }
    });
  };

  if (object.material) {
    applyToMaterial(object.material);
  }

  if (typeof object.traverse === 'function') {
    object.traverse((child) => {
      if (child.isMesh && child.material) {
        applyToMaterial(child.material);
      }
    });
  }

  if (object.userData?.material_settings) {
    object.userData.material_settings.albedo = colorValue;
  }
  if (isResolvedHexColor(colorValue)) {
    object.userData = object.userData || {};
    object.userData.material_settings = {
      ...(object.userData.material_settings || {}),
      albedo: colorValue,
    };
  }
}

export function isParticleType(type) {
  return PARTICLE_TYPES.includes(String(type || '').toLowerCase());
}

export function isLightType(type) {
  return LIGHT_TYPES.includes(String(type || '').toLowerCase());
}

export function isShapeType(type) {
  return SHAPE_TYPES.includes(String(type || '').toLowerCase());
}
